// Author: Claude
// Orchestrator: psinclair

import {formatDateTime} from "@/utils/formatter"
// account fields (subset of au.com.ajg.ssn.model.MemberSummaryDetailDto) that
// decide which account operations are currently applicable to a member
export interface MemberAccountState {
  accountStatus: string | null
  effectiveEmail: string | null
  mustChangePassword: boolean
  failedAuthenticationAttempts: number
  mfaType: string | null
  // password cleared: disabled by staff or locked at activation
  loginDisabled: boolean
  neverLoggedIn: boolean
  // reason account actions aren't available (not a SuperScore client), or null
  accountActionsBlocker: string | null
  // server computed reason the welcome email can't be sent, null when it can
  welcomeEmailBlocker: string | null
  // when the latest welcome email was sent, null when none is recorded
  welcomeEmailLastSentAt: string | null
  // server computed reason the member can't be deleted, null when it can
  deleteBlocker: string | null
  // latest calculator/profiler completion, null when there is nothing to reset
  projectionModelled: string | null
  contributionCalculated: string | null
  ttrCalculated: string | null
  riskProfileAssessed: string | null
  insuranceAssessed: string | null
}
export type AccountActionKey =
  | "resetActivation"
  | "sendWelcomeEmail"
  | "unlock"
  | "passwordReset"
  | "resetMfa"
  | "disableLogin"
  | "enableLogin"
  | "deleteMember"
  | "resetInsuranceCalculator"
  | "resetRiskProfiler"
  | "resetProjectionModeller"
  | "resetContributionCalculator"
  | "resetTtrCalculator"
// ids of the member detail fields that host an action button
export type AccountRowId =
  | "member"
  | "email"
  | "accountStatus"
  | "activations"
  | "mfa"
  | "failedLogins"
  | "passwordExpiry"
  | "projectionModelled"
  | "contributionCalculated"
  | "riskProfileAssessed"
  | "insuranceAssessed"
  | "ttrCalculated"
export interface AccountAction {
  key: AccountActionKey
  // button tooltip and confirmation title
  label: string
  // small button shown inside this field label of the member detail dialog
  button: string
  rowId: AccountRowId
  // shown in the confirmation prompt (may include member specific details)
  description: (m: MemberAccountState) => string
  // immediate/irreversible consequence highlighted in the confirmation prompt
  warning?: string
  // endpoint for the member (POST unless method says otherwise)
  url: (memberId: string) => string
  // reason this action can't be used right now, or null when it can
  unavailableReason: (m: MemberAccountState) => string | null
  // fixed query parameters sent with the request
  params?: Record<string, string>
  // only resetActivation supports optionally sending the activation email
  supportsSendEmail?: boolean
  // also usable by the Employer role (everything else is staff/adviser only)
  employerAllowed?: boolean
  // only usable by the Staff/Admin roles (not advisers)
  adminOnly?: boolean
  method?: "post" | "delete"
  // the member no longer exists afterwards, so don't reload it
  removesMember?: boolean
  success: string
}
export const NOT_PERMITTED = "Not available for your role"
const LOGIN_DISABLED = "Member login is disabled"
const NO_EMAIL = "Member has no email address"
export const hasEmail = (m: MemberAccountState): boolean => (m.effectiveEmail ?? "").trim() !== ""
const AUTH_PATH = "/api/authentication"
export const ACCOUNT_ACTIONS: Array<AccountAction> = [
  {
    key: "resetActivation",
    button: "RESET",
    rowId: "activations",
    label: "Reset activation",
    description: () =>
      "Resets the member to a not-yet-activated state. A new activation code (valid 40 days) replaces their password, and activity history (logins, downloads, assessments), failed logins and activation attempts are cleared.",
    warning: "The member's current password stops working and can't be recovered.",
    url: id => `${AUTH_PATH}/resetMemberActivation/${id}`,
    unavailableReason: m =>
      m.loginDisabled && !m.neverLoggedIn ? `${LOGIN_DISABLED} - enable login instead` : null,
    supportsSendEmail: true,
    success: "Activation reset",
  },
  {
    key: "sendWelcomeEmail",
    button: "WELCOME",
    rowId: "email",
    label: "Send welcome email",
    description: (m: MemberAccountState): string => {
      const lastSent =
        m.welcomeEmailLastSentAt === null
          ? ""
          : ` A welcome email was last sent ${formatDateTime(m.welcomeEmailLastSentAt)}.`
      return `Sends the activation code and Benefit Central registration link to ${
        m.effectiveEmail ?? "the member"
      }. The member's account is not changed.${lastSent}`
    },
    url: id => `/api/members/sendWelcomeEmail/${id}`,
    unavailableReason: m => m.welcomeEmailBlocker,
    employerAllowed: true,
    success: "Welcome email sent",
  },
  {
    key: "unlock",
    button: "UNLOCK",
    rowId: "failedLogins",
    label: "Unlock account",
    description: m =>
      `Clears the failed login count (${m.failedAuthenticationAttempts}) so the member can try logging in again. Their password is not changed.`,
    url: id => `${AUTH_PATH}/memberAccount/${id}/unlock`,
    unavailableReason: m =>
      m.failedAuthenticationAttempts > 0 ? null : "Member has no failed login attempts",
    success: "Account unlocked",
  },
  {
    key: "passwordReset",
    button: "RESET",
    rowId: "passwordExpiry",
    label: "Send password reset",
    description: m =>
      `Emails a temporary password (valid for 48 hours) to ${
        m.effectiveEmail ?? "the member"
      }. The member must set a new password at next login, and failed logins are cleared.`,
    warning:
      "The member's current password stops working immediately. If the email is wrong or not received they will be locked out.",
    url: id => `${AUTH_PATH}/memberAccount/${id}/passwordReset`,
    unavailableReason: (m: MemberAccountState): string | null => {
      if (m.loginDisabled) return LOGIN_DISABLED
      return hasEmail(m) ? null : NO_EMAIL
    },
    success: "Password reset email sent",
  },
  {
    key: "resetMfa",
    button: "RESET",
    rowId: "mfa",
    label: "Reset MFA",
    description: () =>
      "Removes the member's multi-factor authentication setup and trusted devices. They will need to set up MFA again.",
    url: id => `${AUTH_PATH}/memberAccount/${id}/resetMfa`,
    unavailableReason: m => (m.mfaType == null ? "Member has no MFA configured" : null),
    success: "MFA reset",
  },
  {
    key: "disableLogin",
    button: "DISABLE",
    rowId: "accountStatus",
    label: "Disable login",
    description: () =>
      "Blocks the member from logging in or using forgot-password. Their password is kept so login can be re-enabled later.",
    url: id => `${AUTH_PATH}/memberAccount/${id}/disableLogin`,
    unavailableReason: m => (m.loginDisabled ? "Member login is already disabled" : null),
    success: "Login disabled",
  },
  {
    key: "enableLogin",
    button: "ENABLE",
    rowId: "accountStatus",
    label: "Enable login",
    description: m =>
      m.neverLoggedIn
        ? "Issues a new activation code (valid 40 days) as the member never activated. Send the welcome email afterwards."
        : "Restores the member's previous password.",
    url: id => `${AUTH_PATH}/memberAccount/${id}/enableLogin`,
    unavailableReason: m => (m.loginDisabled ? null : "Member login is already enabled"),
    success: "Login enabled",
  },
  {
    key: "deleteMember",
    button: "DELETE",
    rowId: "member",
    label: "Delete member",
    description: () =>
      "Deletes the member and all their data. This runs through the inactive members batch job, so it may be skipped (e.g. never-delete or group insurance members) and can take a few minutes.",
    warning: "This can't be undone.",
    url: id => `/api/members/${id}`,
    unavailableReason: m => m.deleteBlocker,
    adminOnly: true,
    method: "delete",
    removesMember: true,
    success: "Delete requested - the member will be removed shortly",
  },
]
const NOTHING_TO_RESET = "Nothing to reset"
const resetActivity = (
  key: AccountActionKey,
  rowId: AccountRowId,
  type: string,
  label: string,
  what: string,
  lastEvent: (m: MemberAccountState) => string | null
): AccountAction => ({
  key,
  button: "RESET",
  rowId,
  label,
  description: () =>
    `Deletes the member's ${what} so they can start again. The member's other data is not changed.`,
  warning: "This can't be undone.",
  url: id => `${AUTH_PATH}/memberAccount/${id}/resetActivity`,
  params: {type},
  unavailableReason: m => (lastEvent(m) === null ? NOTHING_TO_RESET : null),
  success: `${label} done`,
})
ACCOUNT_ACTIONS.push(
  resetActivity(
    "resetProjectionModeller",
    "projectionModelled",
    "PROJECTION_MODELLER",
    "Reset projection modeller",
    "completed projection modeller events",
    m => m.projectionModelled
  ),
  resetActivity(
    "resetContributionCalculator",
    "contributionCalculated",
    "CONTRIBUTION_CALCULATOR",
    "Reset contribution calculator",
    "completed contribution calculator events",
    m => m.contributionCalculated
  ),
  resetActivity(
    "resetRiskProfiler",
    "riskProfileAssessed",
    "RISK_PROFILER",
    "Reset risk profiler",
    "saved risk profile answers and completed assessments",
    m => m.riskProfileAssessed
  ),
  resetActivity(
    "resetInsuranceCalculator",
    "insuranceAssessed",
    "INSURANCE_CALCULATOR",
    "Reset insurance calculator",
    "saved insurance calculator inputs (expenses, family, debts, assets, other insurances) and completed assessments",
    m => m.insuranceAssessed
  ),
  resetActivity(
    "resetTtrCalculator",
    "ttrCalculated",
    "TTR_CALCULATOR",
    "Reset TTR calculator",
    "completed TTR calculator events",
    m => m.ttrCalculated
  )
)
// null when the action can be run, otherwise the reason it is disabled
export function actionDisabledReason(
  action: AccountAction,
  member: MemberAccountState,
  canManage: boolean,
  isEmployer = false,
  isAdmin = false
): string | null {
  const permitted =
    action.adminOnly === true
      ? isAdmin
      : canManage || (isEmployer && action.employerAllowed === true)
  return permitted
    ? member.accountActionsBlocker ?? action.unavailableReason(member)
    : NOT_PERMITTED
}
export const accountEmailUrl = (memberId: string): string =>
  `${AUTH_PATH}/memberAccount/${memberId}/email`
export const accountAuditUrl = (memberId: string): string =>
  `${AUTH_PATH}/memberAccount/${memberId}/audit`
export interface AccountAuditEntry {
  at: string
  action: string
  detail: string | null
  performedBy: string | null
}
// valid when blank/null (clears the override; a clearable q-input emits null)
// or a plausible email address
export const isValidEmailInput = (value: string | null): boolean => {
  const trimmed = (value ?? "").trim()
  return trimmed === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)
}
